import {
  ApartmentCategoryEnum,
  BuildingTypeEnum,
  EnergyRating,
  Facilities,
  HouseCategoryEnum,
  LandCategoryEnum,
  ListingTypeEnum,
  OfficeCategoryEnum,
  OfficeTypeEnum,
  Orientation,
  PurposeEnum,
  TypeEnum,
  View,
} from "@/types/types";
import { v4 as uuidv4 } from "uuid";

export interface ListingData {
  listing_id: string;
  purpose: PurposeEnum;
  type: TypeEnum;
  category:
    | ApartmentCategoryEnum
    | HouseCategoryEnum
    | LandCategoryEnum
    | OfficeCategoryEnum
    | null;
  price: number;
  title: string;
  web_tour_url: string;
  presentation_video_data: {
    video_url: string;
    thumbnail_url: string;
  };
  photos: string[];
  number_of_rooms: number;
  number_of_full_bathrooms: number;
  number_of_bathrooms: number;
  number_of_bedrooms: number;
  interior_space: number;
  lot_size: number;
  levels: string;
  total_structure_area: number;
  year_built: number;
  buyers_fee: number;
  construction_materials: string;
  map_location: { lat: number; lon: number } | null;
  city: string;
  country: string;
  region: string;
  address_basename: string;
  address_type: string;
  address_language: string;
  address_number: string;
  sub_region: string;
  country_code: string;
  description: string;
  // interior_details: InteriorDetails;
  // exterior_details: ExteriorDetails;
  // other_details: OtherDetails;
  building_type: BuildingTypeEnum | null;
  epc: EnergyRating;
  facilities: Facilities;
  view: View;
  orientation: Orientation;
  new_development: boolean;
  has_luxury_finishes: boolean;
  is_luxury: boolean;
  office_type: OfficeTypeEnum | null;
  listing_type: ListingTypeEnum;
}

export interface ChangedFields {
  listing_id: boolean;
  purpose: boolean;
  type: boolean;
  category: boolean;
  price: boolean;
  title: boolean;
  web_tour_url: boolean;
  presentation_video_data: boolean;
  photos: boolean;
  number_of_rooms: boolean;
  number_of_full_bathrooms: boolean;
  number_of_bathrooms: boolean;
  number_of_bedrooms: boolean;
  interior_space: boolean;
  lot_size: boolean;
  levels: boolean;
  total_structure_area: boolean;
  year_built: boolean;
  buyers_fee: boolean;
  construction_materials: boolean;
  map_location: boolean;
  city: boolean;
  country: boolean;
  region: boolean;
  sub_region: boolean;
  address_basename: boolean;
  address_type: boolean;
  address_language: boolean;
  address_number: boolean;
  country_code: boolean;
  description: boolean;
  interior_details: {
    rooms: boolean;
    appliances: boolean;
    more: boolean;
  };
  exterior_details: {
    parking: boolean;
    outdoors: boolean;
    more: boolean;
  };
  other_details: {
    community_hoa: boolean;
    financial: boolean;
    more: boolean;
  };
  status: false;
  building_type: boolean;
  facilities: boolean;
  epc: boolean;
  view: boolean;
  orientation: boolean;
  new_development: boolean;
  has_luxury_finishes: boolean;
  is_luxury: boolean;
  office_type: boolean;
  supported_locales: false;
  listing_type: ListingTypeEnum;
}

export const initialChangedFields: ChangedFields = {
  listing_id: false,
  purpose: false,
  type: false,
  category: false,
  price: false,
  title: false,
  web_tour_url: false,
  presentation_video_data: false,
  photos: false,
  number_of_rooms: false,
  number_of_full_bathrooms: false,
  number_of_bathrooms: false,
  number_of_bedrooms: false,
  interior_space: false,
  lot_size: false,
  levels: false,
  total_structure_area: false,
  year_built: false,
  buyers_fee: false,
  construction_materials: false,
  map_location: false,
  city: false,
  country: false,
  region: false,
  address_basename: false,
  address_type: false,
  address_language: false,
  address_number: false,
  sub_region: false,
  country_code: false,
  description: false,
  interior_details: {
    rooms: false,
    appliances: false,
    more: false,
  },
  exterior_details: {
    parking: false,
    outdoors: false,
    more: false,
  },
  other_details: {
    community_hoa: false,
    financial: false,
    more: false,
  },
  status: false,
  building_type: false,
  facilities: false,
  epc: false,
  view: false,
  orientation: false,
  new_development: false,
  has_luxury_finishes: false,
  is_luxury: false,
  office_type: false,
  supported_locales: false,
  listing_type: ListingTypeEnum.ESSENTIAL,
};

export const initialState: ListingData = {
  listing_id: `${uuidv4()}-${Math.floor(Date.now() / 1000)}`,
  purpose: PurposeEnum.FORSALE,
  type: TypeEnum.APARTMENT,
  category: null,
  price: 0,
  web_tour_url: "",
  presentation_video_data: {
    video_url: "",
    thumbnail_url: "",
  },
  title: "",
  photos: [],
  number_of_rooms: 0,
  number_of_full_bathrooms: 0,
  number_of_bathrooms: 0,
  number_of_bedrooms: 0,
  interior_space: 0,
  lot_size: 0,
  levels: "",
  total_structure_area: 0,
  year_built: 0,
  buyers_fee: 0,
  construction_materials: "",
  map_location: null,
  city: "",
  country: "",
  region: "",
  sub_region: "",
  address_basename: "",
  address_type: "",
  address_language: "",
  address_number: "",
  country_code: "",
  description: "",
  // interior_details: {
  //   rooms: "",
  //   appliances: "",
  //   more: "",
  // },
  // exterior_details: {
  //   parking: "",
  //   outdoors: "",
  //   more: "",
  // },
  // other_details: {
  //   community_hoa: "",
  //   financial: "",
  //   more: "",
  // },
  building_type: null,
  epc: "N/A",
  facilities: {
    has_parking: false,
    has_pool: false,
    has_fitness_center: false,
    has_elevator: false,
    has_concierge: false,
    has_security: false,
    has_public_roof_deck: false,
    has_roof_deck: false,
    has_garage: false,
    has_waterfront: false,
    has_balcony: false,
    has_furniture: false,
    has_garden: false,
    has_wine_cellar: false,
    has_ac: false,
    has_sauna: false,
    has_gated_community: false,
    is_pet_friendly: false,
    has_utilities_included: false,
    has_disabled_access: false,
    is_ready_to_move_in: false,
    has_gas: false,
    has_electricity: false,
    has_water: false,
    has_sewage: false,
    has_cafeteria: false,
    has_conference_room: false,
    has_public_transport: false,
    has_hoa: false,
    has_intercom: false,
    has_dishwasher: false,
    has_internet: false,
    has_terrace: false,
    has_barbecue: false,
    has_guest_suites: false,
    has_guest_house: false,
    has_valet: false,
    has_alarm: false,
    has_dining_room: false,
    has_ev_charging: false,
    has_hot_tub: false,
    has_washing_machine: false,
    has_tennis_court: false,
    has_yoga: false,
  },
  view: {
    sea_view: false,
    mountain_view: false,
    countryside_view: false,
    city_view: false,
  },
  orientation: {
    north: false,
    south: false,
    east: false,
    west: false,
  },
  new_development: false,
  has_luxury_finishes: false,
  is_luxury: false,
  office_type: null,
  listing_type: ListingTypeEnum.ESSENTIAL,
};

interface InteriorDetails {
  rooms: string;
  appliances: string;
  more: string;
}
interface ExteriorDetails {
  parking: string;
  outdoors: string;
  more: string;
}
interface OtherDetails {
  community_hoa: string;
  financial: string;
  more: string;
}
